import { eUserRole } from 'src/providers/UserProvider/User.enums';
import {
  ACCOUNT_HEALTH_EXTERNAL_TARGETS,
  ALERT_WINDOW_RANK,
  ALL_ROLES,
  CALENDAR_RECONNECT_TARGET,
  LEAD_AGENT_CAPACITY_WARNING_THRESHOLD,
  LEAD_AGENT_CONFIG_TARGET,
  LEAD_AGENT_SHUTDOWN_TARGET,
  LEAD_AGENT_SURFACES,
  LEAD_AGENT_USAGE_TARGET,
  OWNER_AND_ADMIN,
  OWNER_ONLY,
  PAST_DUE_FINAL_DAY,
  PAST_DUE_GENERIC_DECLINE_CODES,
  PAST_DUE_SUBSCRIPTION_TARGET,
  PAST_DUE_WARN_DAY,
  PLAN_PAUSE_SUBSCRIPTION_TARGET,
  QUOTA_WARNING_THRESHOLD,
  resolveQuotaSubscriptionTarget,
  SELLER_ONLY,
  SUBSCRIPTION_PROMO_TARGET,
} from './AlertCenter.constants';
import {
  tAlertCopy,
  tAlertCta,
  tAlertDeclaration,
  tAlertProminence,
  tAlertSnapshot,
} from './AlertCenter.types';
import { LEAD_AGENT_CAP_RAISE_ALERT_ID } from './LeadAgentCapRaiseNotice.types';
import {
  getMetaPricingMessage,
  META_PRICING_ALERT_ID,
  META_PRICING_BALANCE_ALERT_ID,
  META_PRICING_TIPS_ALERT_ID,
  type tMetaPricingMessage,
} from './MetaPricingNotice.types';
import { formatMetaPricingAmount } from './MetaPricingNotice.functions';
import { META_TOKEN_INVALID_ALERT_ID } from './MetaTokenInvalidNotice.types';
import { wapiDisconnectionCopy } from './WapiDisconnection.copy';
import { isLiveWapiNotice } from './WapiDisconnection.functions';

/**
 * The authoritative tier assignment (plan § Alert Register). Declaration order
 * within a priority is the tie-break, so this array's order is meaningful and
 * must stay stable — R4.
 */

/**
 * AGI-428: whether the Meta-pricing source resolved THIS message. The source
 * serves one notice at a time (the newest live message wins), so at most one
 * of the three families can be derived.
 */
function isMetaPricingMessageLive(
  snapshot: tAlertSnapshot,
  message: tMetaPricingMessage,
): boolean {
  const source = snapshot.metaPricingNotice;
  return (
    source.status === 'ready' &&
    source.value !== null &&
    getMetaPricingMessage(source.value.key) === message
  );
}

/**
 * AGI-428: the bell row of either message. Same shape for both (the October
 * figure, and "hoy no pagas" for `cobro_nuevo`); only the copy prefix changes.
 *
 * Sin payload no hay cuerpo. Los dos bodyKey llevan {{amount}} y
 * {{currency}}, asi que nombrar uno sin valores que ofrecer imprimiria los
 * placeholders crudos al usuario: es justo lo que vigila el barrido de
 * interpolacion en AlertCenter.functions.test. El titulo se sostiene solo, y
 * la familia no puede estar viva en ese estado de todos modos.
 */
function metaPricingRowCopy(
  snapshot: tAlertSnapshot,
  prefix: 'metaPricing.row' | 'metaPricing.tips.row',
): tAlertCopy {
  const payload = snapshot.metaPricingNotice.value?.payload ?? null;
  const values: Record<string, string | number> = payload
    ? {
        amount: formatMetaPricingAmount(
          payload.amountOctober,
          payload.currency,
        ),
        currency: payload.currency,
      }
    : {};
  const bodyKey = payload
    ? payload.copyVariant === 'cobro_nuevo'
      ? `${prefix}.bodyNewCharge`
      : `${prefix}.body`
    : undefined;
  return {
    titleKey: `${prefix}.title`,
    bodyKey,
    values,
    cta: { labelKey: `${prefix}.cta`, kind: 'detail' },
  };
}

/** A plan limit of 0, NaN or Infinity is not a usable denominator. */
function hasUsableLimit(snapshot: tAlertSnapshot): boolean {
  const limit = snapshot.activeLeadsLimit;
  return typeof limit === 'number' && Number.isFinite(limit) && limit > 0;
}

function quotaRatio(snapshot: tAlertSnapshot): number | null {
  if (!hasUsableLimit(snapshot)) return null;
  const count = snapshot.activeLeadsCount;
  if (typeof count !== 'number' || !Number.isFinite(count)) return null;
  return count / (snapshot.activeLeadsLimit as number);
}

/**
 * R13b — the occurrence segment. Keyed on the billing PERIOD, not the plan
 * name: a plan name is stable across periods, so keying on it would make an
 * acknowledgment permanent and the alert would never return.
 */
function billingPeriod(snapshot: tAlertSnapshot): string | null {
  return snapshot.billingPeriodId;
}

/**
 * M6/M8 (U4) — the quota CTA per role, shared by both quota tiers. A seller
 * cannot buy capacity but CAN free it: their CTA opens the global archive
 * modal in place (`ModalsProvider`, mounted app-wide). Owner/admin navigate to
 * the exact subscription view the legacy banners already use — add-on modal
 * for Business/Advanced, plans view for Growth — label matched to the
 * destination the same way (`increaseLimitButton` vs `upgradeButton` parity).
 */
/**
 * The violation types the account-health copy and CTA tables actually map,
 * mirroring the legacy banner's `VIOLATION_BANNER_STYLES` keys (lowercased).
 * Anything else resolves to the fallback variant.
 */
const KNOWN_ACCOUNT_HEALTH_VIOLATIONS = [
  'spam',
  'scam',
  'tobacco',
  'scripted_bm',
  'user_initiated_calls_low_pickup_rate',
];

/**
 * The account-health copy-key variant for a status/violation pair — the ONE
 * mapping shared by this declaration's copy resolver and the detail renderer,
 * so the two can never disagree on which variant the user is looking at.
 * Mirrors the legacy banner's fallback: an unmapped violation degrades to
 * `violations.fallback`, never to a raw i18n key.
 */
export function resolveAccountHealthVariant(
  status: string | null,
  violation: string | null,
): string {
  const normalized = violation?.toLowerCase() ?? null;
  if (normalized) {
    return KNOWN_ACCOUNT_HEALTH_VIOLATIONS.includes(normalized)
      ? `violations.${normalized}`
      : 'violations.fallback';
  }
  return `status.${(status ?? 'unknown').toLowerCase()}`;
}

function quotaCta(snapshot: tAlertSnapshot, role: eUserRole): tAlertCta {
  if (role === eUserRole.SELLER) {
    return {
      labelKey: 'quota.archiveCta',
      kind: 'action',
      action: 'archive-leads',
    };
  }
  return {
    labelKey: snapshot.isLeadLimitAddonEligiblePlan
      ? 'quota.increaseCta'
      : 'quota.upgradeCta',
    kind: 'navigate',
    target: resolveQuotaSubscriptionTarget(snapshot),
  };
}

/**
 * KTD16 — the vencido alert's decay phase, and since RET-1957 the input to BOTH
 * its prominence and its placement. One function so the day boundaries exist
 * once: a second copy of `<= 7` is how the alert ends up loud on the page and
 * quiet in the bell on the same day. Null while the elapsed days are unknown,
 * which degrades to the bell (`slot`) rather than to the page.
 */
function leadAgentExpiredPhase(
  snapshot: tAlertSnapshot,
): tAlertProminence | null {
  const days = snapshot.leadAgentDaysSinceExpiry;
  if (days === null || days < 0) return null;
  if (days <= 7) return 'prominent';
  if (days <= 14) return 'compact';
  if (days <= 30) return 'discreet';
  return 'retired';
}

/**
 * The EFFECTIVE urgency of a pending pre-acceptance — the one function both the
 * copy and the placement read.
 *
 * One function for the same reason `leadAgentExpiredPhase` is one: a second
 * copy of this fallback is how the alert ends up shouting a countdown on the
 * page while the bell shows the calm sentence, or worse, taking the page with
 * copy that never mentions why. The fallback is not cosmetic — a countdown
 * without a number renders "faltan  días".
 *
 * Thresholds are NOT defined here. The variant arrives already computed by the
 * acceptance provider (`resolveBannerVariant`), which is also what the legacy
 * banner reads, so the two surfaces cannot drift apart. See
 * `leadAgentAcceptanceUrgency` on the snapshot for why the window is 14 days.
 */
function leadAgentAcceptanceUrgency(
  snapshot: tAlertSnapshot,
): 'noDate' | 'fomo' | 'urgent' {
  const days = snapshot.leadAgentDaysUntilPayment;
  const hasCountdown =
    days !== null && days >= 0 && snapshot.leadAgentPaymentDate !== null;
  if (!hasCountdown) return 'noDate';
  const urgency = snapshot.leadAgentAcceptanceUrgency;
  return urgency === 'fomo' || urgency === 'urgent' ? urgency : 'noDate';
}

// ─── The past-due ladder (U12/U13 · RET-1269) ────────────────────────────────

/** Which rung of the quiet half of the ladder a snapshot is on. */
type tPastDueRung = 'soft' | 'warn';

/**
 * The arrears counter, or null when there is nothing truthful to say.
 *
 * `0` is a REAL value — the first day of arrears — so the guard is `< 0` and
 * never a truthiness check. A NEGATIVE count means the front counter and the
 * backend anchor are keyed to different clocks; the snapshot preserves that
 * sign deliberately (U11) so the disagreement stays visible, and the ladder's
 * job is to say nothing rather than to invent a rung out of it.
 */
function pastDueDays(snapshot: tAlertSnapshot): number | null {
  if (!snapshot.subscriptionPastDue) return null;
  const days = snapshot.daysPastDue;
  if (typeof days !== 'number' || !Number.isFinite(days) || days < 0) {
    return null;
  }
  return days;
}

/**
 * KTD12 — the rung of the ramp, as a RANGE.
 *
 * ONE function for the placement resolver, the prominence resolver and the
 * copy resolver, for the reason `leadAgentExpiredPhase` is one function: a
 * second copy of `>= 5` is how an alert ends up orange on the page while the
 * bell still paints it yellow, on the same day.
 *
 * Null past the final threshold, which is what hands the ladder over to
 * `past-due-final` without either declaration having to know about the other.
 */
function pastDueEarlyRung(snapshot: tAlertSnapshot): tPastDueRung | null {
  /*
   * 🔴 Past due CONFIRMED but the day count unknown => the gentlest rung, never
   * silence.
   *
   * `pastDueDays` returns null for a missing, non-finite or NEGATIVE count, and
   * negative is not hypothetical: while `days_past_due` is still rolling out,
   * the provider's fallback derives from `endDate` — the very field this ticket
   * exists to stop trusting — which for a past-due subscription is always a
   * cycle in the FUTURE, so the derivation is always negative. Returning null
   * here on that input left the ladder dark while the modal chain was already
   * gated off by the flag: flag ON, field unpublished, and the customer got NO
   * signal at all. That is the same outcome KTD10 forbids, reached from the
   * other side of the flag.
   *
   * `soft` is the honest landing: its copy (owner and seller both) claims
   * nothing about elapsed time — only `warn` and `final` interpolate a count —
   * so showing it without a number says exactly what we know. We never guess
   * upward: an unknown count must not reach `final`, which asserts the account
   * is about to be cancelled.
   */
  if (!snapshot.subscriptionPastDue) return null;

  const days = pastDueDays(snapshot);
  if (days === null) return 'soft';
  if (days >= PAST_DUE_FINAL_DAY) return null;
  return days >= PAST_DUE_WARN_DAY ? 'warn' : 'soft';
}

function isPastDueFinal(snapshot: tAlertSnapshot): boolean {
  const days = pastDueDays(snapshot);
  return days !== null && days >= PAST_DUE_FINAL_DAY;
}

/**
 * KTD1 — the cancellation bound, but only while it is still ahead.
 *
 * Stripe's date is a CEILING ("a mas tardar el"), not an appointment, and a
 * PAST_DUE subscription still exists at day 20 and at day 60 (U4's dataset).
 * A rung that kept reciting a date already gone would be the exact class of
 * untruth this plan exists to remove, so past the bound every date-bearing
 * string is swapped for a dateless one.
 *
 * Null is also the NORMAL state of the manual-collection cohort, which has no
 * cancellation date at all — same degradation, one branch.
 */
function pastDueDeadline(snapshot: tAlertSnapshot): string | null {
  if (snapshot.pastDueCancellationPassed) return null;
  return snapshot.pastDueCancellationDate;
}

/**
 * Which failure the customer is actually looking at — and therefore what to
 * tell them to do about it.
 *
 * 🔴 "Actualiza tu tarjeta" is WRONG for two thirds of failures. Stripe's own
 * dashboard, last three months: `insufficient_funds` is 69% of failures by
 * count against 2% for `expired_card`. In the dominant case the card is
 * perfectly valid and the account is empty, so telling the customer to replace
 * it sends them to fix something that is not broken — Stripe agrees, returning
 * `advice_code: try_again_later`.
 *
 * An unrecognised code renders NO line: the copy degrades to the plain wording
 * rather than guessing at a code Stripe shipped last week. Manual collection
 * renders none either, because nothing was ever charged to a card on this
 * invoice and a code left over from an earlier automatic cycle would name a
 * failure that did not happen.
 */
function pastDueReasonKey(snapshot: tAlertSnapshot): string | null {
  if (snapshot.pastDueAttempted === false) return null;
  const code = snapshot.pastDueDeclineCode;
  if (typeof code !== 'string' || code.trim() === '') return null;
  // Brand and last4 identify WHICH card failed, which matters when the
  // customer has several. Either one missing drops both: "Tu  ···4242" and
  // "Tu Visa ···" are worse than naming no card at all.
  const namesCard = !!snapshot.pastDueCardBrand && !!snapshot.pastDueCardLast4;
  if (code === 'insufficient_funds') {
    return namesCard
      ? 'pastDue.reason.insufficientFunds'
      : 'pastDue.reason.insufficientFundsNoCard';
  }
  if (code === 'expired_card') {
    return namesCard
      ? 'pastDue.reason.expiredCard'
      : 'pastDue.reason.expiredCardNoCard';
  }
  return PAST_DUE_GENERIC_DECLINE_CODES.includes(code)
    ? 'pastDue.reason.generic'
    : null;
}

/**
 * What Stripe will do NEXT, as an observed fact rather than a static sentence.
 *
 * Three branches, and the middle one is why this is not a fixed string: once
 * Stripe has exhausted its schedule there IS no next attempt, and a rung that
 * kept promising one would overshoot its own deadline. "No habra mas intentos
 * automaticos de cobro" states something already decided.
 *
 * ⚠️ Never a TOTAL. "Intento 3 de 8" is not derivable — the ceiling lives in a
 * dashboard setting the API does not expose and a console edit can change.
 */
function pastDueRetryKey(snapshot: tAlertSnapshot): string | null {
  // `attempted: false` is the manual-collection cohort: no automatic charge is
  // scheduled, so there is no retry state to report.
  if (snapshot.pastDueAttempted === false) return null;
  if (snapshot.pastDueNextPaymentAttempt) {
    const count = snapshot.pastDueAttemptCount;
    return typeof count === 'number' && Number.isFinite(count) && count > 0
      ? 'pastDue.retry.scheduled'
      : 'pastDue.retry.scheduledNoCount';
  }
  return snapshot.pastDueAttempted === true ? 'pastDue.retry.exhausted' : null;
}

/**
 * The interpolation inputs for the OWNER rungs. Every key is omitted rather
 * than emptied when its fact is unknown, so a copy branch that still references
 * it fails loudly in the i18n sweep instead of rendering "a mas tardar el ".
 */
function pastDueOwnerValues(
  snapshot: tAlertSnapshot,
): Record<string, string | number> {
  const values: Record<string, string | number> = {};
  const deadline = pastDueDeadline(snapshot);
  /*
   * ⚠️ `amount` LOOKS UNUSED on the day 9+ rung and is not. No string in
   * `pastDue.owner.final.*` interpolates it (only the day 0-4 title does), so
   * a sweep for dead interpolation values would flag it — but `U14`'s detail
   * renderer (`PastDueFinalDetail`) reads it from the live alert's published
   * values to put the figure in front of the pay button. Deleting it here
   * empties that line silently: no key goes missing, no test for THIS file
   * fails, the amount just stops rendering where it matters most.
   */
  if (snapshot.pastDueAmount) values.amount = snapshot.pastDueAmount;
  if (deadline) values.deadline = deadline;
  if (snapshot.pastDueCardBrand && snapshot.pastDueCardLast4) {
    values.cardBrand = snapshot.pastDueCardBrand;
    values.cardLast4 = snapshot.pastDueCardLast4;
  }
  const retryKey = pastDueRetryKey(snapshot);
  if (retryKey === 'pastDue.retry.scheduled') {
    // i18next's reserved plural name: "1 intentos" is broken Spanish, so the
    // retry line ships as `_one`/`_other`. The owner rungs carry no other
    // counted string, so `count` is unambiguous here.
    values.count = snapshot.pastDueAttemptCount as number;
  }
  if (
    retryKey?.startsWith('pastDue.retry.scheduled') &&
    snapshot.pastDueNextPaymentAttempt
  ) {
    values.nextAttempt = snapshot.pastDueNextPaymentAttempt;
  }
  return values;
}

/**
 * R9 — the SELLER's inputs, and the disclosure decision made structural.
 *
 * The amount, the card identity and the decline reason are never assembled
 * here at all, rather than assembled and then filtered downstream: a value
 * that is never put in the object cannot leak through a copy branch somebody
 * adds later. The DATE is not withheld — it is the operationally useful half
 * for someone planning their week, and the approved mockup always showed it.
 */
function pastDueSellerValues(
  snapshot: tAlertSnapshot,
): Record<string, string | number> {
  const values: Record<string, string | number> = {};
  const deadline = pastDueDeadline(snapshot);
  if (deadline) values.deadline = deadline;
  const days = pastDueDays(snapshot);
  if (days !== null) values.count = days;
  return values;
}

/**
 * The pay control. External to Stripe's hosted page when the BFF publishes
 * one; otherwise Ajustes -> Suscripcion, where the payment method lives.
 *
 * The fallback is not cosmetic: `withActionableCta` strips a target-less
 * `external` CTA before rendering, so without it the owner of a workspace
 * whose `openInvoiceSource` is `none` would meet a past-due notice with no way
 * to act on it — on the rungs where paying matters most.
 */
function pastDuePayCta(snapshot: tAlertSnapshot): tAlertCta {
  const url = snapshot.pastDueInvoiceUrl;
  if (url) {
    return { labelKey: 'pastDue.cta.pay', kind: 'external', target: url };
  }
  return {
    labelKey: 'pastDue.cta.manage',
    kind: 'navigate',
    target: PAST_DUE_SUBSCRIPTION_TARGET,
  };
}

export const ALERT_REGISTRY: tAlertDeclaration[] = [
  // ---------------------------------------------------------------- P0
  // D4 (2026-08-19) — every P0 is GLOBAL (no `surfaces` scoping) and delivers
  // through the single wide strip, arbitrated by `windowRank`. A critical that
  // blocks the operation blocks it on every page, so scoping one to the pages
  // where its banner used to live would hide the block exactly where the user
  // went to work around it.
  {
    id: 'plan-pause-active',
    priority: 'p0',
    placement: 'wide',
    windowRank: ALERT_WINDOW_RANK['plan-pause-active'],
    roles: ALL_ROLES,
    dismissible: false,
    derive: (s) => s.planPauseActive === true,
    copy: (s, role) => ({
      titleKey: 'planPauseActive.title',
      bodyKey: s.planPauseEndsAt
        ? 'planPauseActive.body'
        : 'planPauseActive.bodyNoDate',
      values: { endsAt: s.planPauseEndsAt ?? '' },
      cta:
        role === eUserRole.OWNER
          ? {
              labelKey: 'planPauseActive.cta',
              kind: 'navigate',
              target: PLAN_PAUSE_SUBSCRIPTION_TARGET,
            }
          : null,
    }),
  },
  {
    id: 'wapi-disconnected',
    priority: 'p0',
    placement: 'wide',
    windowRank: ALERT_WINDOW_RANK['wapi-disconnected'],
    roles: ALL_ROLES,
    // `wapi` exclusivity — an account that is down outranks one that was never
    // set up. Declared here, and first, so this P0 wins whenever both
    // predicates read true and the workspace is never told to connect a number
    // at the same moment it is told its number is gone.
    exclusivityGroup: 'wapi',
    dismissible: false,
    /*
     * Two triggers, one notice family (INT-1653):
     * - the row is DISCONNECTED (a retired account, cases 0-7, 9 and 10). Both
     *   facts come off the same row, so the declaration reads both; the
     *   provider's resolver never pairs a true flag with a null cause.
     * - the row is live but Meta is moving the number or just gave it back
     *   (8a, 8b, 8c). Only `wapiDisconnection` reports those; the backend
     *   sends them once INT-1660 ships, so until then this half never fires.
     */
    derive: (s) =>
      (s.wapiDisconnected && s.wapiDisconnectCause !== null) ||
      isLiveWapiNotice(s.wapiDisconnection),
    // Each reconnection is its own episode, so a card closed for one comes
    // back for the next (`occurrence` is the reconnection's `at`).
    period: (s) => s.wapiDisconnection?.occurrence ?? null,
    // INT-1653 — copy, buttons and variant by Meta's cause and by role: the
    // owner and the admin get the remedy, everyone else the note.
    copy: (s, role) =>
      wapiDisconnectionCopy({ disconnection: s.wapiDisconnection, role }),
  },
  {
    id: 'ww-banned',
    priority: 'p0',
    placement: 'wide',
    windowRank: ALERT_WINDOW_RANK['ww-banned'],
    roles: ALL_ROLES,
    dismissible: false,
    derive: (s) => s.whatsAppWebErrorCode === 'BANNED',
    // Product decision (Ariel, 2026-08-20): the ban is Meta's call and the
    // appeal happens with Meta, but Leadsales does not wash its hands — the
    // CTA opens LEADSALES support, which guides the customer through the
    // appeal. The copy states both halves explicitly (not our doing, we help
    // anyway); never a bare external link that reads as "go away".
    copy: () => ({
      titleKey: 'wwBanned.title',
      bodyKey: 'wwBanned.body',
      cta: { labelKey: 'wwBanned.cta', kind: 'intercom' },
    }),
  },
  {
    id: 'account-health',
    priority: 'p0',
    placement: 'wide',
    windowRank: ALERT_WINDOW_RANK['account-health'],
    roles: ALL_ROLES,
    dismissible: false,
    derive: (s) =>
      s.canAccessWhatsAppApi &&
      s.accountHealthStatus !== null &&
      s.accountHealthStatus !== 'OK',
    // KTD15 — one declaration, many variants: status x violation type, each
    // with its own copy and its own CTA kind (support widget vs Meta link).
    // U4 — the external variants carry the exact hrefs the legacy
    // AccountHealthBanner links to (INT-1040/INT-1101 tables); a variant with
    // no entry in the target map escalates through Intercom, mirroring the
    // legacy banner's intercom CTAs.
    copy: (s) => {
      const variant = resolveAccountHealthVariant(
        s.accountHealthStatus,
        s.accountHealthViolation,
      );
      const externalTarget = ACCOUNT_HEALTH_EXTERNAL_TARGETS[variant];
      return {
        titleKey: `accountHealth.${variant}.title`,
        bodyKey: `accountHealth.${variant}.body`,
        cta: {
          labelKey: `accountHealth.${variant}.cta`,
          kind: externalTarget ? 'external' : 'intercom',
          target: externalTarget,
        },
      };
    },
  },
  {
    /*
     * INT-1522 (Fase 3/3 de INT-715): a Meta token went invalid, per
     * meta-messenger-service's own debug_token check (INT-1520) -- fed here
     * via workspace-service's workspace_notices (AGI-428), NOT account-health
     * above: that family is WhatsApp API's Meta-reported health (spam/scam/
     * etc violations), a different channel and a different signal.
     *
     * P0/wide/non-dismissible, same tier as wapi-disconnected: this is a
     * live channel silently failing to send or receive, not a settings nag
     * — an owner/admin should see it the moment they log in.
     *
     * Channels reviewer follow-up: no per-channel copy. A meta_account's
     * Instagram page shares the SAME access token as its Facebook page (see
     * EventService::metaTokenHealthChangedEvent()'s docblock in
     * meta-messenger-service), so a dead token always takes down both
     * channels together -- naming one channel in the title would at best be
     * incomplete and at worst wrong.
     *
     * CTA opens MetaConnectionModal directly, pinned at its reconnect step
     * (`reconnect-meta` action, see tAlertCtaAction), rather than escalating
     * to Intercom -- self-service reconnect already existed, it was just
     * unreachable from outside the dashboard. `routing-conflict` below still
     * uses `intercom` deliberately, for a case that genuinely has none.
     *
     * NOT a `navigate` CTA to the Dashboard (tried first, twice rejected in
     * review): all P0s are global, so this alert also renders ON the
     * Dashboard, where a query-string-only navigate() does not remount the
     * page; and even fixed, the integration still reads CONNECTED (the
     * token is invalid, not disconnected), so the modal needs telling not
     * to auto-jump to its disconnect step. An in-place action, same as
     * `connect-wapi` above, sidesteps both.
     */
    id: META_TOKEN_INVALID_ALERT_ID,
    priority: 'p0',
    placement: 'wide',
    windowRank: ALERT_WINDOW_RANK[META_TOKEN_INVALID_ALERT_ID],
    roles: OWNER_AND_ADMIN,
    dismissible: false,
    dependsOn: ['metaTokenInvalidNotice'],
    derive: (s) =>
      s.metaTokenInvalidNotice.status === 'ready' &&
      s.metaTokenInvalidNotice.value !== null,
    copy: () => ({
      titleKey: 'metaTokenInvalid.title',
      bodyKey: 'metaTokenInvalid.body',
      cta: {
        labelKey: 'metaTokenInvalid.cta',
        kind: 'action',
        action: 'reconnect-meta',
      },
    }),
  },
  {
    id: 'quota-reached',
    priority: 'p0',
    placement: 'wide',
    windowRank: ALERT_WINDOW_RANK['quota-reached'],
    roles: ALL_ROLES,
    exclusivityGroup: 'quota',
    dismissible: false,
    period: billingPeriod,
    derive: (s) => {
      const ratio = quotaRatio(s);
      return s.isQuotaEligiblePlan && ratio !== null && ratio >= 1;
    },
    copy: (s, role) => ({
      titleKey: 'quota.reached.title',
      bodyKey: 'quota.reached.body',
      values: { planName: s.planName ?? '' },
      cta: quotaCta(s, role),
    }),
  },
  {
    id: 'la-usage-capped',
    priority: 'p0',
    placement: 'wide',
    windowRank: ALERT_WINDOW_RANK['la-usage-capped'],
    // D4 — global like every P0: the LEAD_AGENT_SURFACES scope moved down to
    // its P1 sibling. Role scoping stays: an asesor cannot act on the cap, so
    // for them the arbitration simply runs without this contender.
    roles: OWNER_AND_ADMIN,
    exclusivityGroup: 'la-usage',
    dismissible: false,
    dependsOn: ['pendingPayment'],
    derive: (s) => (s.leadAgentCapacityPercent ?? 0) >= 100,
    copy: (s, role) => ({
      titleKey: 'leadAgentUsage.capped.title',
      bodyKey: `leadAgentUsage.capped.${role === eUserRole.OWNER ? 'owner' : 'admin'}.body`,
      values: { capacity: s.leadAgentCapacityPercent ?? 100 },
      // The checkout lives in the detail (`LeadAgentUsageDetail` mounts
      // `UpgradeTierModal`). "Ampliar Lead Agent" used to navigate to the
      // Smart Leadbot config page, which carries no upgrade affordance — the
      // legacy banner had already fixed that by mounting the modal in place,
      // and the port to the Alert Center undid it.
      cta:
        role === eUserRole.OWNER
          ? { labelKey: 'leadAgentUsage.capped.cta', kind: 'detail' }
          : null,
    }),
  },

  /*
   * U12 (RET-1269) — the last rung of the owner ladder.
   *
   * `wide` because from day 9 the question is no longer "did you notice" but
   * "do you know this ends": the strip is the only surface that reaches an
   * owner who works inside the Messenger and never opens the bell. Per KTD5 it
   * is a COUNTDOWN, not a claim of suspension — nothing blocks access before
   * cancellation, so "cuenta bloqueada" would be false, and false is exactly
   * what this plan exists to remove.
   */
  {
    id: 'past-due-final',
    priority: 'p0',
    placement: 'wide',
    windowRank: ALERT_WINDOW_RANK['past-due-final'],
    roles: OWNER_AND_ADMIN,
    exclusivityGroup: 'past-due',
    dismissible: false,
    derive: isPastDueFinal,
    copy: (s) => {
      const deadline = pastDueDeadline(s);
      return {
        // Only the TITLE carries the bound, so only the title has a dateless
        // twin: the body says what paying saves, which is true either way.
        titleKey: deadline
          ? 'pastDue.owner.final.title'
          : 'pastDue.owner.final.titleNoDate',
        bodyKey: 'pastDue.owner.final.body',
        extraBodyKeys: [pastDueReasonKey(s), pastDueRetryKey(s)].filter(
          (key): key is string => key !== null,
        ),
        values: pastDueOwnerValues(s),
        // U14 mounts the payment flow in this alert's own detail. `detail` is
        // the only kind that can carry a charge (RET-1956): a route cannot,
        // and the strip has hosted its own detail since `la-usage-capped`
        // needed one.
        cta: { labelKey: 'pastDue.cta.pay', kind: 'detail' },
      };
    },
  },
  /*
   * U13 (RET-1269) — the seller's last rung.
   *
   * A SECOND declaration rather than a role branch inside the one above,
   * because `roles` and `priority` are fixed fields: one entry cannot be an
   * owner's payable P0 and a seller's notice at the same time. It shares the
   * exclusivity group safely — `applyExclusivity` runs AFTER the role filter,
   * so disjoint audiences can never contend.
   *
   * Loud for a reason of its own: a seller who loses the workspace mid
   * conversation, having read one unchanging line for two weeks, is a worse
   * outcome than being told plainly. No amount, no CTA — they cannot pay, and
   * naming a figure they did not agree to is the disclosure decision R9 makes.
   *
   * ⚠️ VIEWER IS A DECLARED NON-GOAL, and the consequence is recorded here so
   * it is not rediscovered. `eUserRole.VIEWER` exists in the enum and in
   * `ALL_ROLES`, but production holds ZERO workspace users with that role
   * (57,092 OWNER / 26,257 SELLER / 13,890 ADMIN / 0 VIEWER, measured
   * 2026-09-10), so covering it would add a role set and an assertion this
   * plan cannot verify against a real user. If the role is ever switched on
   * for customers, those users lose workspace access at cancellation having
   * seen no past-due notice at all — the exact defect these two declarations
   * fix for sellers. The fix is ONE TOKEN: add `eUserRole.VIEWER` to both
   * seller role sets. Whoever activates the role owns that line.
   */
  {
    id: 'past-due-seller-final',
    priority: 'p0',
    placement: 'wide',
    windowRank: ALERT_WINDOW_RANK['past-due-seller-final'],
    roles: SELLER_ONLY,
    exclusivityGroup: 'past-due',
    dismissible: false,
    derive: isPastDueFinal,
    copy: (s) => {
      const deadline = pastDueDeadline(s);
      return {
        titleKey: deadline
          ? 'pastDue.seller.final.title'
          : 'pastDue.seller.final.titleNoDate',
        // `count` is the arrears counter here (the seller rungs carry no retry
        // line), so i18next resolves `body_one` / `body_other`.
        bodyKey: 'pastDue.seller.final.body',
        values: pastDueSellerValues(s),
        cta: null,
      };
    },
  },

  // ---------------------------------------------------------------- P1
  /*
   * U12 (RET-1269) — days 0-8 of the owner ladder, as ONE declaration.
   *
   * Two rungs, one entry: `placement` and `prominence` both read
   * `pastDueEarlyRung`, so the alert sits quietly in the bell in yellow while
   * the charge is merely late (days 0-4 — 76% of recoveries) and CLIMBS onto
   * the page in orange once it has been late for most of a week. Splitting it
   * in two would split the exclusivity group and the acknowledgment key with
   * it, and the customer would meet the same fact twice (the RET-1957 lesson).
   *
   * DECLARED FIRST among the P1s deliberately: `AlertInPage` renders only the
   * first matching alert, and a lapsed payment outranks a Lead Agent decay —
   * one ends the account, the other ends a feature.
   *
   * ⚠️ Not dismissible at any rung (KTD6). The Centro de Avisos page has not
   * shipped, so a dismissed notice could never be found again; the day 5-8
   * banner's close is a SESSION-scoped visual close on one component, not an
   * acknowledgment (see `AlertInPage`).
   */
  {
    id: 'past-due-early',
    priority: 'p1',
    placement: (s) => (pastDueEarlyRung(s) === 'warn' ? 'in-page' : 'slot'),
    prominence: pastDueEarlyRung,
    roles: OWNER_AND_ADMIN,
    exclusivityGroup: 'past-due',
    dismissible: false,
    derive: (s) => pastDueEarlyRung(s) !== null,
    copy: (s) => {
      const rung = pastDueEarlyRung(s);
      const deadline = pastDueDeadline(s);
      const extraBodyKeys = [pastDueReasonKey(s), pastDueRetryKey(s)].filter(
        (key): key is string => key !== null,
      );
      const values = pastDueOwnerValues(s);
      if (rung === 'warn') {
        return {
          // The titles of the two rungs DIFFER, and must: they share tier p1
          // and therefore share its glyph, so hue alone would be the only
          // separation between them (WCAG 1.4.1 / KTD12).
          titleKey: 'pastDue.owner.warn.title',
          bodyKey: deadline
            ? 'pastDue.owner.warn.body'
            : 'pastDue.owner.warn.bodyNoDate',
          extraBodyKeys,
          values,
          cta: pastDuePayCta(s),
        };
      }
      return {
        titleKey: s.pastDueAmount
          ? 'pastDue.owner.soft.title'
          : 'pastDue.owner.soft.titleNoAmount',
        bodyKey: 'pastDue.owner.soft.body',
        extraBodyKeys,
        values,
        cta: pastDuePayCta(s),
      };
    },
  },
  {
    id: 'quota-near',
    priority: 'p1',
    placement: 'slot',
    roles: ALL_ROLES,
    exclusivityGroup: 'quota',
    dismissible: true,
    period: billingPeriod,
    derive: (s) => {
      const ratio = quotaRatio(s);
      return (
        s.isQuotaEligiblePlan &&
        ratio !== null &&
        ratio >= QUOTA_WARNING_THRESHOLD &&
        ratio < 1
      );
    },
    copy: (s, role) => ({
      titleKey: 'quota.near.title',
      bodyKey: 'quota.near.body',
      values: {
        planName: s.planName ?? '',
        percentage: Math.round((quotaRatio(s) ?? 0) * 100),
      },
      cta: quotaCta(s, role),
    }),
  },
  {
    id: 'la-usage-near',
    priority: 'p1',
    placement: 'slot',
    roles: OWNER_AND_ADMIN,
    surfaces: LEAD_AGENT_SURFACES,
    exclusivityGroup: 'la-usage',
    dismissible: true,
    dependsOn: ['pendingPayment'],
    derive: (s) => {
      const pct = s.leadAgentCapacityPercent;
      return (
        pct !== null &&
        pct >= LEAD_AGENT_CAPACITY_WARNING_THRESHOLD &&
        pct < 100
      );
    },
    copy: (s, role) => ({
      titleKey: 'leadAgentUsage.near.title',
      bodyKey: `leadAgentUsage.near.${role === eUserRole.OWNER ? 'owner' : 'admin'}.body`,
      values: { capacity: s.leadAgentCapacityPercent ?? 0 },
      // Same as its capped sibling: the tier upgrade is in the detail.
      cta:
        role === eUserRole.OWNER
          ? { labelKey: 'leadAgentUsage.near.cta', kind: 'detail' }
          : null,
    }),
  },
  {
    id: 'la-trial-cap',
    priority: 'p1',
    placement: 'slot',
    roles: OWNER_AND_ADMIN,
    surfaces: LEAD_AGENT_SURFACES,
    exclusivityGroup: 'la-trial',
    /*
     * SOLO SE PUEDE CERRAR MIENTRAS EL BOT RESPONDE (revisión adversaria,
     * 2026-09-02).
     *
     * Poner la fase en la llave —abajo— evitó que confirmar el avistamiento
     * silenciara el tope. No evitó lo peor: que el tope se silenciara a SÍ
     * MISMO. Y ahí el silencio dura hasta el fin de la prueba, porque la fase
     * `capped` es una sola ocurrencia desde que topa; mientras tanto
     * `trialCapActive` apaga la pre-aceptación y el aviso de activación no
     * aplica. Resultado: ningún aviso del Lead Agent, durante semanas, con
     * "tus leads no están recibiendo respuesta" siendo literalmente cierto.
     *
     * La asimetría lo delata: el MISMO hecho en un cliente que paga es
     * `la-usage-capped`, que es P0 y no se puede cerrar. La cohorte de prueba
     * había heredado un permiso que el catálogo no le da — `MLA-52` concede el
     * descarte al aviso de VENCIDO en particular, y §18 pone "bot pausado
     * ahora" en lo alto de la jerarquía.
     *
     * El avistamiento sí se puede cerrar: ahí el bot sigue contestando y es
     * una advertencia, no un hecho consumado.
     */
    dismissible: (s) =>
      s.leadAgentTrialCapVariant !== 'capped' &&
      s.leadAgentTrialCapVariant !== 'extendedCapped',
    /*
     * LA FASE ES LA OCURRENCIA (Fanny, 2026-09-02).
     *
     * El predicado es el mismo para "cerca del cap" y para "topado", así que
     * con una sola llave marcar como visto el AVISO DE ADVERTENCIA silenciaba
     * después el del BOT PAUSADO: el episodio nunca terminaba entre los dos, la
     * confirmación no se podaba, y el dueño se quedaba sin enterarse de que sus
     * leads dejaron de recibir respuesta.
     *
     * Con la fase en la llave cada situación se confirma por separado, que es
     * para lo que existe este segmento ("cuando el predicado puede volver a
     * dispararse"). Nunca es null mientras el aviso viva: si no hay variante,
     * `derive` es falso y el aviso no existe.
     */
    period: (s) => s.leadAgentTrialCapVariant,
    derive: (s) => s.leadAgentTrialCapReached,
    // Copy follows Fanny's prototype (the UI authority named by RET-1633), same
    // as LeadAgentTrialCapBanner. The title is role-scoped so the Owner reads
    // the prototype's first-person line and the Admin reads the neutral one.
    // NO days-remaining value: the prototype's capped state carries the
    // trial-end DATE, and the old "{{days}} días" line is what a paused
    // customer read as "52 días" (reported 2026-08-19).
    //
    // VARIANT-scoped since the compact pass: the predicate above fires for
    // four disjoint situations and only `capped` is a paused agent. Pinned to
    // the capped wording, an owner still being answered at 80% of the trial
    // read "tu Lead Agent se pausó" in the row, in the bell and in the modal
    // header — the panel shows title and body, so this was not a modal-only
    // defect. Null variant degrades to `capped`: the alert is live, so the
    // safe reading is the one that says the agent stopped.
    copy: (s, role) => {
      const variant = s.leadAgentTrialCapVariant ?? 'capped';
      const scope = `leadAgentTrialCap.${variant}`;
      return {
        titleKey: `${scope}.${role === eUserRole.OWNER ? 'owner' : 'admin'}.title`,
        /*
         * THE DEADLINE RIDES IN THE BODY (Fanny, 2026-09-01).
         *
         * This surface never carried it — the trial-end date only ever showed
         * inside the detail — so an owner two days from the end read "extiende
         * su capacidad para que no se pause antes de que termine tu prueba"
         * with no way to know that "termine" was the day after tomorrow. Two
         * rules were unmet at once: `MLA-70` asks the sighting notice to show
         * the trial-end date, and `MLA-53` asks the winning alert's copy to
         * mention the secondary fact — here, the deadline.
         *
         * That gap is also what made the last-days variants look necessary.
         * With the deadline in the copy the cap can simply win, keep both of
         * its doors, and still tell the customer the clock (see
         * `resolveTrialCapBannerVariant`).
         *
         * Falls back to the dateless body when the day count is missing: "en
         *  días" is worse than not saying it.
         */
        bodyKey: `${scope}.${role === eUserRole.OWNER ? 'owner' : 'admin'}.${
          // `extendedCapped` is exempt: its whole message is "the self-serve
          // extension is spent, talk to support", and it carries no dated
          // variant to fall back from.
          variant !== 'extendedCapped' &&
          (typeof s.leadAgentTrialDaysRemaining !== 'number' ||
            s.leadAgentTrialDaysRemaining < 0)
            ? 'bodyNoDate'
            : 'body'
        }`,
        values: { count: s.leadAgentTrialDaysRemaining ?? 0 },
        // Was `navigate` to the Smart Leadbot config page (RET-1956 kept it as
        // "the only path left" to that page). Two defects came out of that: the
        // page cannot unlock anything, so "Desbloquear ahora" pointed away from
        // the money; and the detail renders its OWN unlock button, so the modal
        // showed the same label twice.
        //
        // `detail` only where the detail HAS something. The countdown variants
        // are the exception the hierarchy carved out: R13 gives them no
        // self-serve unlock, so `LeadAgentTrialCapDetail` paints body and
        // nothing else for them — sending the CTA there opened an empty
        // dialog. They keep the config page, which is at least where the agent
        // lives.
        // `extendedCapped` is the third case, and the same defect one variant
        // over: its label is "Contactar a soporte", so sending it to the detail
        // made the support button open a dialog containing another support
        // button (Fanny, 2026-09-01). There is nothing to decide here — the
        // extension is spent and no self-serve option remains — so the CTA is
        // the support bubble itself. The detail is still reachable from the
        // row's text area for whoever wants to read the explanation first.
        cta:
          role === eUserRole.OWNER
            ? {
                labelKey: `${scope}.cta`,
                kind: variant === 'extendedCapped' ? 'intercom' : 'detail',
              }
            : null,
      };
    },
  },
  {
    id: 'la-expired',
    priority: 'p1',
    // RET-1957 — the decay phase decides the SURFACE too, not just the visual
    // weight: a workspace whose Lead Agent expired days ago is losing money
    // now, and the bell is a place the owner has to remember to open. The
    // alert takes the in-page surface while it is `prominent` and falls back
    // to the bell once it decays, so the nagging fades on the same clock the
    // prominence does. It does NOT become a P0: the wide strip is for
    // non-dismissible criticals (R13), and MLA-52 keeps "dismissible + decays"
    // out of that tier.
    placement: (s) =>
      leadAgentExpiredPhase(s) === 'prominent' ? 'in-page' : 'slot',
    roles: OWNER_AND_ADMIN,
    surfaces: LEAD_AGENT_SURFACES,
    exclusivityGroup: 'la-state',
    dismissible: true,
    // KTD9 — the server-side dismissal is an INPUT to the predicate, not a
    // session acknowledgment. Shipping it session-scoped would resurrect an
    // alert the owner already dismissed permanently.
    derive: (s) => s.leadAgentExpired && s.leadAgentDismissedAt === null,
    // KTD16 — prominence decays; `retired` suppresses selection entirely.
    prominence: leadAgentExpiredPhase,
    copy: (_s, role) => ({
      titleKey: 'leadAgentExpired.title',
      bodyKey: `leadAgentExpired.${role === eUserRole.OWNER ? 'owner' : 'admin'}.body`,
      // RET-1956 — the money lives in the detail. Sending "Reactivar" to the
      // Smart Leadbot config page pointed the Owner at a screen with no way to
      // pay: `ReactivateLeadAgentModal` was orphaned when RET-1879 turned the
      // legacy banner off. `LeadAgentExpiredDetail` mounts it again.
      cta:
        role === eUserRole.OWNER
          ? { labelKey: 'leadAgentExpired.cta', kind: 'detail' }
          : null,
    }),
  },
  {
    id: 'la-shutdown',
    priority: 'p1',
    placement: 'slot',
    roles: OWNER_ONLY,
    surfaces: LEAD_AGENT_SURFACES,
    exclusivityGroup: 'la-state',
    dismissible: true,
    derive: (s) => s.leadAgentPausedWithinTrial,
    copy: (s) => ({
      titleKey: 'leadAgentShutdown.title',
      bodyKey: 'leadAgentShutdown.body',
      values: { date: s.leadAgentShutdownDate ?? '' },
      // Parity restored: the legacy banner navigated to Ajustes → Suscripción,
      // where the reactivation card is. The port sent it to the config page,
      // which has no way to reactivate anything.
      cta: {
        labelKey: 'leadAgentShutdown.cta',
        kind: 'navigate',
        target: LEAD_AGENT_SHUTDOWN_TARGET,
      },
    }),
  },
  {
    /*
     * AGI-352 — a revoked calendar connection is a CONDITION, not an event.
     * It shipped first as a `calendar.connection.revoked` tray notification
     * (AGI-306/AGI-346): one dismissible row, gone forever on dismiss, while
     * the Lead Agent went on booking blind — able to double-book every lead it
     * scheduled. A predicate that stays true has to be an alert; the tray's
     * copy resolver now returns null for that event (see
     * `AgendaNotifications.functions.ts`).
     *
     * NOT dismissible, on the `user-inactive` precedent (R15): the condition
     * actively harms the operation while true and the owner can fix it in one
     * consent flow, so the nagging is the function. A dismissible version
     * would just rebuild the original bug with acknowledgment semantics — one
     * click and the alert is silent for the whole revoked period.
     *
     * `in-page` for the same reason: a non-dismissible alert that lives only
     * behind the bell is invisible (D12 keeps non-clearable alerts out of the
     * badge), and this one asks for action now. Mirrors the fresh phase of
     * `la-expired`, minus the decay — the wire carries no revoked-at date to
     * decay on, and unlike a lapsed payment this is not a state the owner may
     * have chosen to live with.
     *
     * `la-state` exclusivity: while the agent itself is expired or paused
     * (`la-expired`, `la-shutdown`, both declared earlier and thus winning the
     * P1 tie), reconnecting the calendar is not the user's next step — the
     * agent could not schedule anyway.
     */
    id: 'calendar-revoked',
    priority: 'p1',
    placement: 'in-page',
    roles: OWNER_AND_ADMIN,
    surfaces: LEAD_AGENT_SURFACES,
    exclusivityGroup: 'la-state',
    dismissible: false,
    // R3b — while the connection state is still loading, neither fire nor
    // treat as absent.
    dependsOn: ['calendarConnectionRevoked'],
    derive: (s) =>
      s.calendarConnectionRevoked.status === 'ready' &&
      s.calendarConnectionRevoked.value === true,
    // Both roles get the CTA: reconnecting is a configuration action on the
    // Lead Agent config page (the `la-trial-activation` rationale), not a
    // purchase, so the admin is not locked out of fixing it.
    copy: () => ({
      titleKey: 'calendarRevoked.title',
      bodyKey: 'calendarRevoked.body',
      cta: {
        labelKey: 'calendarRevoked.cta',
        kind: 'navigate',
        target: CALENDAR_RECONNECT_TARGET,
      },
    }),
  },
  {
    id: 'la-transfer-pending',
    priority: 'p1',
    placement: 'slot',
    roles: ALL_ROLES,
    surfaces: LEAD_AGENT_SURFACES,
    dismissible: true,
    // RET-1764 — the invoice wins over the capacity alerts: it has a deadline
    // and a billing consequence, while the cap resolves itself at the cut-off.
    // R3a: suppression only applies on surfaces this alert itself reaches.
    suppresses: ['la-usage-near', 'la-usage-capped'],
    dependsOn: ['pendingPayment'],
    derive: (s) =>
      s.pendingPayment.status === 'ready' && s.pendingPayment.value === true,
    copy: (s) => ({
      titleKey: 'leadAgentTransfer.title',
      bodyKey: 'leadAgentTransfer.body',
      values: {
        amount: s.leadAgentInvoiceAmount ?? '',
        date: s.leadAgentInvoiceDueDate ?? '',
      },
      // Parity fix (U4): the legacy banner's CTA is an EXTERNAL link to the
      // hosted invoice, not in-app navigation. A null URL leaves the CTA
      // target-less, which the actionable-strip drops — a "Ver factura" that
      // goes nowhere is worse than no button.
      cta: {
        labelKey: 'leadAgentTransfer.cta',
        kind: 'external',
        target: s.leadAgentInvoiceUrl ?? undefined,
      },
    }),
  },
  {
    id: 'la-acceptance',
    // NOT raised with the urgency, on purpose. `la-expired`'s note says it
    // best: the wide strip is for non-dismissible criticals (R13), and this is
    // a dismissible commercial decision. Promoting it would seat a sales nudge
    // above real operational blockers in the arbitration.
    priority: 'p1',
    /*
     * The surface escalates instead (Fanny, 2026-09-01), the mirror image of
     * `la-expired`: that alert DECAYS from the page to the bell as the loss
     * ages, this one CLIMBS to the page as the deadline closes. Same mechanism,
     * opposite direction.
     *
     * Only `urgent` (≤3d) takes the page. The bell is a place the owner has to
     * remember to open, and in the last days this alert is the ONLY way to buy
     * the add-on — a purchase path nobody opens is not a purchase path.
     * Informativo and FOMO stay in the bell so the escalation still means
     * something when it happens.
     *
     * Reads the same resolver the copy does, so the page can never take a
     * surface with copy that does not explain the urgency.
     */
    placement: (s) =>
      leadAgentAcceptanceUrgency(s) === 'urgent' ? 'in-page' : 'slot',
    /*
     * Declared for the same reason `la-expired` declares it, and with the same
     * caveat: NOTHING STYLES IT YET. `AlertInPage` stamps `data-prominence` and
     * no stylesheet reads the attribute, so today this changes the DOM and not
     * a single pixel. It is here so the escalation is complete the day that
     * seam is wired, and so the intent is inspectable meanwhile — not because
     * the urgent alert currently looks heavier than the calm one.
     */
    prominence: (s) =>
      leadAgentAcceptanceUrgency(s) === 'urgent' ? 'prominent' : 'compact',
    roles: OWNER_ONLY,
    surfaces: LEAD_AGENT_SURFACES,
    dismissible: true,
    /*
     * LA URGENCIA ES LA OCURRENCIA (Fanny, 2026-09-02).
     *
     * El predicado es "sigue sin responder", que se mantiene cierto todo el
     * camino de informativo a urgente. Con una sola llave, marcar como visto a
     * 20 días silenciaba también el "se desactiva en 3 días" — y como este
     * aviso es hoy el ÚNICO camino de compra, eso equivalía a cerrarse la
     * puerta sin querer. La cuenta regresiva no lo traía de vuelta porque FOMO
     * y urgente son el mismo aviso con otra copy, no avisos distintos.
     *
     * Reusa el mismo resolver que la copy y la superficie, para que las tres
     * cosas no puedan discrepar sobre en qué urgencia estamos.
     */
    period: (s) => leadAgentAcceptanceUrgency(s),
    derive: (s) => s.leadAgentAcceptancePending,
    /*
     * Three urgencies on one clock, adopted from the legacy banner rather than
     * re-derived (`MLA-53` §18: urgente ≤3d > FOMO ≤~9d > informativo). The
     * Alert Center shipped with only the informative copy, so an owner three
     * days from losing the agent read the same calm sentence as one who had a
     * month — the countdown existed in the provider and nothing consumed it.
     *
     * The FOMO/urgent copy is the banner's COMPACT variant verbatim: it was
     * already written for a narrow surface, already translated, and already
     * carries the day count. A fourth wording would have been a fourth thing
     * to keep true.
     *
     * `noDate` is the FALLBACK, not a phase of the funnel. Read the name
     * literally: it is what this alert says when it cannot state the deadline.
     *
     * It used to be called the "informative" variant, written for ">14 days
     * from the charge" — and that situation CANNOT occur:
     * `isInformativeBannerSuppressed` turns the whole alert off for it, so the
     * first thing an owner ever sees is the FOMO countdown at D-14 (Fanny,
     * 2026-06-19; showing it earlier was the bug). Keeping the old name here
     * invited the next reader to believe there is a calm early stage that
     * ships. There isn't.
     *
     * What DOES reach this branch is narrow and real: the day count and the
     * formatted date are derived by two different functions from the same
     * field, so one can parse where the other does not. Then the countdown has
     * no number to show, and this calm wording is what goes out instead of
     * "faltan  días".
     */
    copy: (s): tAlertCopy => {
      const urgency = leadAgentAcceptanceUrgency(s);
      if (urgency !== 'noDate') {
        return {
          titleKey: `leadAgentAcceptance.${urgency}.title`,
          bodyKey: `leadAgentAcceptance.${urgency}.body`,
          values: {
            count: s.leadAgentDaysUntilPayment ?? 0,
            date: s.leadAgentPaymentDate ?? '',
          },
          cta: {
            labelKey: `leadAgentAcceptance.${urgency}.cta`,
            kind: 'detail' as const,
          },
        };
      }
      return {
        titleKey: 'leadAgentAcceptance.noDate.title',
        bodyKey: 'leadAgentAcceptance.noDate.body',
        values: { tier: s.leadAgentTier ?? '' },
        // RET-1956 — same reason as la-expired: the accept/decline flow
        // (ConfirmTierModal, DeclineModal) has no home outside the detail
        // since RET-1879, and the config page cannot pre-accept an add-on.
        cta: {
          labelKey: 'leadAgentAcceptance.noDate.cta',
          kind: 'detail' as const,
        },
      };
    },
  },
  {
    id: 'user-inactive',
    priority: 'p1',
    // R15 — deliberate exception: stays full width above the board, in amber.
    // The nagging is the function; an inactive advisor receives no leads.
    placement: 'in-page',
    roles: ALL_ROLES,
    dismissible: false,
    derive: (s) => s.userInactive,
    copy: () => ({
      titleKey: 'userInactive.title',
      bodyKey: 'userInactive.body',
      // Same one-click fix the in-page banner offers (R15 keeps that banner);
      // from the panel the action resolves through the shared availability
      // hook, so both surfaces flip the same state.
      cta: {
        labelKey: 'userInactive.cta',
        kind: 'action',
        action: 'restore-availability',
      },
    }),
  },
  {
    /*
     * The workspace bought the WhatsApp API and never connected a number.
     * The step it needs is the connect flow, and support is deliberately not on
     * the path — nothing is broken, the setup is simply unfinished.
     *
     * DECLARED LAST among the in-page P1s, again because `AlertInPage` renders
     * only the first match: an unfinished setup is the least urgent thing on it,
     * and it must never take the page from a lapsed payment or a dying agent.
     *
     * `in-page` rather than the bell, for `calendar-revoked`'s reason: it is
     * not dismissible, and D12 keeps non-clearable alerts out of the badge, so
     * behind the bell it would be invisible to the very workspaces that have
     * never opened the integrations page.
     *
     * Not dismissible because there is nothing to acknowledge — the nudge ends
     * when the number is connected, which is the outcome it exists to produce.
     */
    id: 'wapi-not-connected',
    priority: 'p1',
    placement: 'in-page',
    roles: ALL_ROLES,
    exclusivityGroup: 'wapi',
    dismissible: false,
    derive: (s) => s.wapiNoAccount,
    copy: () => ({
      titleKey: 'wapiNotConnected.title',
      bodyKey: 'wapiNotConnected.body',
      // The same connect flow `wapi-disconnected` opens; for this customer it
      // is the first run rather than a repair.
      cta: {
        labelKey: 'wapiNotConnected.cta',
        kind: 'action',
        action: 'connect-wapi',
      },
    }),
  },
  {
    /*
     * INT-1516 — Meta did not activate the number: the activation window closed
     * on a conclusive "not activated" (the integrations row at
     * ACTIVATION_FAILED). Nothing the number receives reaches the inbox until
     * the customer reconnects, so the reconnect is the primary action (KTD10),
     * the same connect flow `wapi-disconnected` opens; U8 starts that modal on
     * the failure screen for a failed row.
     *
     * P1 in-page like `wapi-not-connected`, and for the same reason: not
     * dismissible, and D12 keeps non-clearable alerts out of the bell's badge,
     * so behind the bell it would reach nobody. The warning is resolved by the
     * record itself (operational, released or replaced), not by the user.
     *
     * DECLARED BEFORE `routing-conflict`, the tie-break between the two P1s in
     * the `wapi` group: `inboundRoutingConflict` passes through on a failed
     * row, and until the number is activated its routing does not matter.
     *
     * No interpolation: the verdict's reason never reaches this copy.
     */
    id: 'wapi-activation-failed',
    priority: 'p1',
    placement: 'in-page',
    roles: ALL_ROLES,
    exclusivityGroup: 'wapi',
    dismissible: false,
    derive: (s) => s.wapiActivationFailed,
    copy: () => ({
      titleKey: 'wapiActivationFailed.title',
      bodyKey: 'wapiActivationFailed.body',
      cta: {
        labelKey: 'wapiActivationFailed.cta',
        kind: 'action',
        action: 'connect-wapi',
      },
    }),
  },
  {
    /*
     * Another workspace's WhatsApp API row wins inbound routing for the same
     * WABA, so this workspace silently receives none of the number's messages.
     *
     * DECLARED AFTER `wapi-not-connected`, the tie-break both in the P1 tier
     * and in the `wapi` group: until a number finishes connecting there is no
     * routing to be in conflict about, so that nudge goes first.
     *
     * Home and Funnels only. Chat already explains this in its own empty state,
     * beside the missing conversations; these two are where the loss is
     * otherwise invisible — neither page looks wrong when inbound messages land
     * somewhere else.
     *
     * Not dismissible: the messages keep going elsewhere until support moves
     * the number, and there is nothing for the customer to acknowledge.
     *
     * The copy resolves with NO interpolation values. The other workspace is
     * frequently an unrelated tenant, and with no values there is nothing for
     * its name or uuid to ride in on.
     */
    id: 'routing-conflict',
    priority: 'p1',
    placement: 'in-page',
    roles: ALL_ROLES,
    surfaces: ['home', 'funnels'],
    exclusivityGroup: 'wapi',
    dismissible: false,
    derive: (s) => s.wapiRoutingConflict,
    copy: () => ({
      titleKey: 'routingConflict.title',
      bodyKey: 'routingConflict.body',
      // Only support can move the number between workspaces, so there is no
      // route and no in-place action to offer — the same ask the chat empty
      // state makes for this fact.
      cta: { labelKey: 'routingConflict.cta', kind: 'intercom' },
    }),
  },
  {
    /*
     * AGI-428 - el aviso del cambio de precios de Meta (1-oct-2026).
     *
     * LAST P1 ON PURPOSE. El orden de declaracion es el desempate dentro de
     * una prioridad (R4), y este aviso es informativo con fecha: no debe
     * ganarle el turno a una cuota llena, a un pago pendiente ni a un agente
     * pausado, que son cosas que el usuario tiene que resolver hoy.
     *
     * `slot` y sin `surfaces`: p0 encenderia la tira global, que esta
     * prohibida para este aviso (decision de Ariel, 15-sep: modal al entrar y
     * campana, sin banner), y sin scope de superficie la fila esta donde sea
     * que el duenno entre. Sin `exclusivityGroup`: no compite con ninguna otra
     * familia, porque no habla del mismo hecho que ninguna.
     *
     * `dismissible: true`, como los demas P1 que piden acuse (Ariel, 16-sep:
     * "no punto, sino numero de notificaciones"). Por D12 eso es lo que lo
     * mete en el NUMERO de la campana y lo saca del punto de atencion: un aviso
     * que no se puede cerrar encendia el punto ambar hasta tres semanas sobre
     * un aviso informativo. El costo aceptado es el que D12 nombra: "Marcar
     * como visto" quita la fila de la campana antes del 5-oct.
     *
     * El checkbox "No volver a mostrar" del modal sigue sin ser un acuse: apaga
     * solo la apertura automatica y la fila sigue en la campana (Unit 7). El
     * acuse, en cambio, SI apaga la apertura automatica: el host lee la lista
     * sin acuses (`AutoOpenNoticeHost`).
     */
    id: META_PRICING_ALERT_ID,
    priority: 'p1',
    placement: 'slot',
    roles: OWNER_AND_ADMIN,
    dismissible: true,
    /*
     * R15 - el acuse sobrevive al fin del episodio, igual que el explicador
     * de la tarjeta roja y por la misma clase de razon: el predicado se apaga
     * por motivos que no tienen que ver con que el usuario lo haya leido. Un
     * refetch en focus que falla deja la fuente en `error`, el pruner lo lee
     * como episodio terminado y devuelve el acuse, y el aviso vuelve a la
     * campana (y al modal automatico) en el siguiente fetch bueno. Aqui no hay
     * re-disparo legitimo que proteger: el aviso es un solo hecho con fecha,
     * con su propio id por cambio de precios, y vence el 5-oct.
     */
    acknowledgmentOutlivesEpisode: true,
    // R3b - mientras la fuente carga, ni dispara ni cuenta como ausente.
    dependsOn: ['metaPricingNotice'],
    /*
     * Solo mientras la fuente resuelve el MENSAJE 1. Desde el 25-sep la misma
     * fuente puede resolver el mensaje 2 (que gana mientras esta vivo), y ese
     * tiene su propia familia justo abajo: una cuenta nunca ve las dos filas.
     */
    derive: (s) => isMetaPricingMessageLive(s, 'impact'),
    /*
     * La fila de la campana dice el monto de octubre, que es el unico numero
     * que decide si vale la pena abrir el detalle. El `cobro_nuevo` ademas
     * aclara que hoy no paga nada, porque para esa cuenta el cargo es nuevo y
     * un monto suelto se lee como un aumento.
     *
     * `kind: 'detail'`: por el contrato del pie en `AlertDetail.registry.ts`,
     * el renderer pinta sus propias acciones (checkbox, Cerrar y CTA), asi que
     * el modal no debe agregar un boton mas.
     *
     * La etiqueta de la fila dice lo que el click hace: abrir el detalle. Hasta
     * el 16-sep repetia la del boton del modal ("Agendar acompannamiento 1:1"),
     * y un link que dice "Agendar" y abre un modal rompe su promesa (revision
     * de Ariel en staging). La accion (sesion 1:1, webinar o preguntas
     * frecuentes) vive en el boton del modal, que conserva las tres etiquetas
     * por urgencia (`META_PRICING_CTA_LABEL_KEYS`).
     */
    copy: (s): tAlertCopy => metaPricingRowCopy(s, 'metaPricing.row'),
  },
  {
    /*
     * AGI-428, mensaje 2 (25-sep): las mejores practicas en video, a los
     * mismos 779 workspaces y con la misma variante de copy del mensaje 1.
     *
     * Mismos atributos que la familia de arriba, y por las mismas razones
     * (ultimo P1, `slot` sin tira, OWNER y ADMIN, acusable con acuse que
     * sobrevive al episodio). Es otra familia, y no la misma con otro copy,
     * por UNA razon: el acuse, "No volver a mostrar" y la marca de sesion van
     * por id, y lo que alguien hizo con el mensaje 1 no puede silenciar un
     * mensaje que todavia no vio.
     */
    id: META_PRICING_TIPS_ALERT_ID,
    priority: 'p1',
    placement: 'slot',
    roles: OWNER_AND_ADMIN,
    dismissible: true,
    acknowledgmentOutlivesEpisode: true,
    dependsOn: ['metaPricingNotice'],
    derive: (s) => isMetaPricingMessageLive(s, 'tips'),
    // La fila no promete sesion 1:1: la accion vive en el boton del modal.
    copy: (s): tAlertCopy => metaPricingRowCopy(s, 'metaPricing.tips.row'),
  },
  {
    /*
     * AGI-428, mensaje 3 (6-oct): el nuevo saldo (creditos convertidos a
     * dinero que no vence, ventana de 7 dias para anuncios Click to WhatsApp)
     * con el video de Dany, a los mismos 779 y con la misma variante de copy.
     *
     * Mismos atributos que las dos familias de arriba y por las mismas
     * razones; otra familia por la misma razon que el mensaje 2: el acuse,
     * "No volver a mostrar" y la marca de sesion van por id.
     *
     * La fila no lleva montos: el mensaje 3 no habla del estimado, y un
     * {{amount}} sin payload que lo llene saldria crudo. Titulo y cuerpo se
     * sostienen solos. Quien no ve el saldo nunca llega aca: la fuente ya
     * viene filtrada (`gateMetaPricingNoticeOnWallet`).
     */
    id: META_PRICING_BALANCE_ALERT_ID,
    priority: 'p1',
    placement: 'slot',
    roles: OWNER_AND_ADMIN,
    dismissible: true,
    acknowledgmentOutlivesEpisode: true,
    dependsOn: ['metaPricingNotice'],
    derive: (s) => isMetaPricingMessageLive(s, 'balance'),
    copy: (): tAlertCopy => ({
      titleKey: 'metaPricing.balance.row.title',
      bodyKey: 'metaPricing.balance.row.body',
      values: {},
      cta: { labelKey: 'metaPricing.balance.row.cta', kind: 'detail' },
    }),
  },

  // ---------------------------------------------------------------- P2
  {
    id: 'la-seller-paused',
    priority: 'p2',
    placement: 'contextual',
    roles: SELLER_ONLY,
    exclusivityGroup: 'la-state',
    dismissible: false,
    dependsOn: ['sellerPaused'],
    derive: (s) =>
      s.sellerPaused.status === 'ready' && s.sellerPaused.value === true,
    copy: (s) => ({
      titleKey: 'leadAgentSellerPaused.title',
      bodyKey: 'leadAgentSellerPaused.body',
      values: { ownerName: s.ownerName ?? '' },
      cta: null,
    }),
  },

  // ---------------------------------------------------------------- P3
  /*
   * U13 (RET-1269) — days 0-8 for the seller, on the same clock as the owner.
   *
   * P3, and NOT because it is dismissible — under KTD6 nothing on this ladder
   * is, and `canAcknowledge` refuses a non-dismissible alert before it ever
   * reaches the tier branch, so a P1 here would be no more clearable and no
   * more countable. The tier buys PLACEMENT IN THE PANEL:
   * `partitionPanelSections` files P1 under "Requieren tu decisión" and P3
   * under "Novedades", and a fact with no action attached belongs in
   * Novedades. The seller's bell signal is the critical dot, never the number.
   *
   * It ESCALATES like the owner's: one quiet fact for days 0-4, then from day 5
   * the stakes by name — the deadline, and that the whole team loses access —
   * so a seller feels the urgency while there is still time to push their
   * administrator, rather than meeting it for the first time on day 9.
   *
   * The explicit `prominence` matters more here than on the owner rung: P3's
   * tier default is info BLUE, so without it the ramp would mean one thing for
   * the person who can pay and another for the person who cannot.
   */
  {
    id: 'past-due-seller',
    priority: 'p3',
    placement: (s) => (pastDueEarlyRung(s) === 'warn' ? 'in-page' : 'slot'),
    prominence: pastDueEarlyRung,
    roles: SELLER_ONLY,
    exclusivityGroup: 'past-due',
    dismissible: false,
    derive: (s) => pastDueEarlyRung(s) !== null,
    copy: (s) => {
      const rung = pastDueEarlyRung(s);
      const deadline = pastDueDeadline(s);
      const values = pastDueSellerValues(s);
      if (rung === 'warn') {
        return {
          titleKey: 'pastDue.seller.warn.title',
          bodyKey: deadline
            ? 'pastDue.seller.warn.body'
            : 'pastDue.seller.warn.bodyNoDate',
          values,
          cta: null,
        };
      }
      return {
        // Different title from the warn rung, for the same WCAG 1.4.1 reason
        // the owner's two differ. The seller's quiet rungs used to carry
        // IDENTICAL copy, which made hue their only difference.
        titleKey: 'pastDue.seller.soft.title',
        bodyKey: 'pastDue.seller.soft.body',
        values,
        cta: null,
      };
    },
  },
  {
    /*
     * INT-1516 — the WhatsApp API record exists, but Meta has not activated
     * its number yet (the integrations row at ACTIVATION_PENDING). Held
     * informs (KTD10): Meta is still confirming the number, and the customer
     * will hear when it is active or has to be reconnected.
     *
     * P3, the info tier: a fact with no action attached. No CTA on purpose:
     * an in-page alert carries one button, which would make "reconnect" the
     * primary action on a record that may still activate; the WhatsApp API
     * page banner offers it as the secondary one.
     *
     * In-page for the reason its failed twin is: not dismissible (it ends when
     * the verdict does), and a non-clearable bell row reaches nobody (D12).
     * Lowest in the `wapi` group, so a routing conflict on the same row takes
     * Home and Funnels, where it is scoped, and this shows everywhere else.
     */
    id: 'wapi-activation-pending',
    priority: 'p3',
    placement: 'in-page',
    roles: ALL_ROLES,
    exclusivityGroup: 'wapi',
    dismissible: false,
    derive: (s) => s.wapiActivationPending,
    copy: () => ({
      titleKey: 'wapiActivationPending.title',
      bodyKey: 'wapiActivationPending.body',
      cta: null,
    }),
  },
  {
    id: 'la-trial-activation',
    priority: 'p3',
    placement: 'slot',
    roles: OWNER_AND_ADMIN,
    surfaces: LEAD_AGENT_SURFACES,
    exclusivityGroup: 'la-trial',
    dismissible: true,
    derive: (s) => s.leadAgentTrialActivatable,
    copy: (_s, role) => ({
      titleKey: 'leadAgentTrialActivation.title',
      bodyKey: `leadAgentTrialActivation.${role === eUserRole.OWNER ? 'owner' : 'admin'}.body`,
      // Exact legacy parity: the activation banner's CTA already navigates to
      // the Smart Leadbot config page (2026-06-22 plan).
      cta:
        role === eUserRole.OWNER
          ? {
              labelKey: 'leadAgentTrialActivation.cta',
              kind: 'navigate',
              target: LEAD_AGENT_CONFIG_TARGET,
            }
          : null,
    }),
  },
  {
    /*
     * RET-2191 — the Lead Agent cap raise (RET-2140), announced once in the
     * bell. P3 because it is a fact with no action attached, which is what
     * "Novedades" holds (`partitionPanelSections`); nothing to decide, only
     * something to know before the upgrade modal shows smaller multiples.
     *
     * Live only while this workspace has the seeded notice. The seed runs
     * AFTER leadbilling's backfill, so the row existing is the proof the new
     * cap is already in place: no plan, usage or entitlement field on the
     * front can say that. The row carries the tier (one body per tier, with
     * that tier's real increase) and whether the raise un-paused the agent.
     *
     * R15 as in the Meta notice: the acknowledgment outlives the episode, so a
     * failed focus refetch (source `error`, read as the episode ending) cannot
     * hand a dismissed notice back.
     */
    id: LEAD_AGENT_CAP_RAISE_ALERT_ID,
    priority: 'p3',
    placement: 'slot',
    roles: OWNER_AND_ADMIN,
    dismissible: true,
    acknowledgmentOutlivesEpisode: true,
    // R3b - mientras la fuente carga, ni dispara ni cuenta como ausente.
    dependsOn: ['leadAgentCapRaiseNotice'],
    derive: (s) =>
      s.leadAgentCapRaiseNotice.status === 'ready' &&
      s.leadAgentCapRaiseNotice.value !== null,
    copy: (s): tAlertCopy => {
      const payload = s.leadAgentCapRaiseNotice.value?.payload ?? null;
      const variant = payload?.wasCapped ? 'resumed' : 'raised';
      return {
        titleKey: `leadAgentCapRaise.${variant}.title`,
        // Without a payload there is no tier to name; the family cannot be
        // live in that state anyway, and the title stands on its own.
        bodyKey: payload
          ? `leadAgentCapRaise.${variant}.body.${payload.tier}`
          : undefined,
        cta: {
          labelKey: 'leadAgentCapRaise.cta',
          kind: 'navigate',
          target: LEAD_AGENT_USAGE_TARGET,
        },
      };
    },
  },
  {
    id: 'billing-migration',
    priority: 'p3',
    placement: 'slot',
    roles: ALL_ROLES,
    // R14b-Q2 (M8) — the migration freezes billing actions users start from
    // Home as much as from the funnel, so both surfaces carry the notice.
    surfaces: ['home', 'funnels'],
    dismissible: true,
    derive: (s) => s.isBillingMigrationInProgress,
    copy: () => ({
      titleKey: 'billingMigration.title',
      bodyKey: 'billingMigration.body',
      cta: null,
    }),
  },
  {
    // U9 (R13, R14, R15 · KTD6/KTD7) — the first red card explained, once.
    //
    // Fires on the board's OWN signal (U8), not on a fetch: the moment a card
    // turns red is the moment the colour needs explaining, and a workspace
    // whose board never reddens never meets this alert at all.
    //
    // `in-page` rather than `contextual` because the board's only contextual
    // anchor — the funnel header chip — holds exactly one alert and
    // `la-seller-paused` already has it. The page surface is free, and it is
    // the banner shape the team reviewed.
    id: 'stage-threshold-explainer',
    priority: 'p3',
    placement: 'in-page',
    roles: ALL_ROLES,
    // The signal is published by a mounted board and cleared on its unmount,
    // so the alert has nothing truthful to say anywhere else.
    surfaces: ['funnels'],
    dismissible: true,
    // R15 — "once dismissed, it does not return". Without this the pruner
    // reads the board's unmount signal as the end of the episode and hands
    // the acknowledgment back. See `tAlertDeclaration`.
    acknowledgmentOutlivesEpisode: true,
    derive: (s) => s.boardHasExpiredLead,
    // Everyone sees it; only the roles that can change the threshold get a
    // way to act (KTD6, session decision). A seller staring at a colour
    // nobody explained is the confusion this exists to prevent — silence for
    // them would be the same bug with better manners.
    copy: (s, role) => {
      const canEditThreshold =
        role === eUserRole.OWNER || role === eUserRole.ADMIN;
      const variant = canEditThreshold ? 'owner' : 'seller';
      return {
        titleKey: `stageThresholdExplainer.${variant}.title`,
        bodyKey: `stageThresholdExplainer.${variant}.body`,
        // Both variants name the lead and how long it has waited: the board
        // already holds both, and a generic "a card turned red" sends the
        // user hunting for which one. Null-safe because the copy resolvers
        // are swept over quiet snapshots by the registry tests — a null must
        // degrade the sentence, never render `{{leadName}}` at the user.
        values: {
          leadName: s.boardExpiredLeadName ?? '',
          // Supplied to BOTH variants so either sentence can name the column;
          // today only the owner one does, matching the reviewed mockup.
          stageName: s.boardExpiredStageName ?? '',
          // Worded by the provider (`formatElapsedWait`): minutes under an
          // hour, hours under a day, then days and hours. The plural lives in
          // that wording, so the bodies carry no `_one`/`_other` of their own.
          elapsed: s.boardExpiredElapsed ?? '',
        },
        cta: canEditThreshold
          ? {
              labelKey: 'stageThresholdExplainer.cta',
              kind: 'action',
              action: 'open-stage-threshold',
            }
          : null,
      };
    },
  },
  {
    id: 'subscription-promo',
    priority: 'p3',
    // R16 — monetization for workspaces with no plan. Stays exactly as it
    // ships today: purple, full width, below the funnel header.
    placement: 'in-page',
    roles: OWNER_ONLY,
    dismissible: false,
    derive: (s) => !s.hasActivePlan,
    copy: (s) => ({
      titleKey: 'subscriptionPromo.title',
      bodyKey: 'subscriptionPromo.body',
      values: {
        total: s.subscriptionPromoTotal ?? '',
        currency: s.subscriptionPromoCurrency ?? '',
      },
      cta: {
        labelKey: 'subscriptionPromo.cta',
        kind: 'navigate',
        target: SUBSCRIPTION_PROMO_TARGET,
      },
    }),
  },
];
